import { useMutation } from '@tanstack/react-query';
import { Avatar, Dropdown } from 'flowbite-react';
import { ArrowLeft } from 'lucide-react';
import { useCallback } from 'react';
import { Link, useNavigate } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { getNameInitials } from '../../../editor/utils/string.utils';
import { useModals } from '../../../hooks/useModals.hook';
import { pathWithParams } from '../../../lib/routing.utils';
import { useSession } from '../../hooks/useSession.hook';
import { AboutModal } from '../../modals/about/aboutModal.component';
import { GeneralFeedbackModal } from '../../modals/feedback/generalFeedbackModal.component';
import { RouteIndex } from '../../routeIndex';

export interface AppHeaderProps {
    pageTitle: string;
    backLink?: string;
    onBackClick?: () => void;
    children?: React.ReactNode;
    leftSlot?: () => React.ReactNode;
}

export const AppHeader: React.FC<AppHeaderProps> = ({
    pageTitle,
    backLink,
    onBackClick,
    children,
    leftSlot
}) => {
    const api = useApi();
    const navigate = useNavigate();
    const modals = useModals();

    const { identity } = useSession();

    const doLogout = useMutation({
        mutationFn: async () => {
            const sessionId = api.client.getSessionId();

            if (sessionId) {
                api.v1.identities.revokeMySession(sessionId);
                api.client.deleteSession();
            }
        },
        onSuccess: () => {
            navigate(pathWithParams(RouteIndex.login, {}));
        }
    });

    const logout = useCallback(() => {
        doLogout.mutate();
    }, []);

    const handleFeedbackClick = useCallback(() => {
        modals.showModal('feedback', GeneralFeedbackModal, {});
    }, []);

    const handleAboutClick = useCallback(() => {
        modals.showModal('about', AboutModal, {});
    }, []);

    return (
        <div className='fixed top-0 left-0 w-full h-14  flex flex-row items-center gap-4 px-3 bg-gray-100 bg-opacity-90 backdrop-blur z-[80]'>
            <div className='w-1/3 flex flex-row items-center'>
                <Link to={RouteIndex.documentList} className="flex flex-row items-center gap-2 hover:bg-white rounded-full transition-colors duration-200 p-1 pr-3">
                    <img src="/assets/ai_korektury_logo.svg" alt="AI KorekturyReadyToRead Korektor" width="574" height="574" className="max-h-[2.5rem] w-auto" />
                </Link>
                <div className='hidden lg:flex flex-row items-center gap-2 ml-2 empty:hidden'>
                    {leftSlot && leftSlot()}
                </div>
            </div>

            <div className='flex-grow flex flex-row items-center justify-center gap-2'>
                {backLink && (
                    <Link to={backLink} className='flex flex-row items-center p-2 bg-transparent hover:bg-white rounded-full transition-colors duration-200'>
                        <ArrowLeft size={20} className='' />
                    </Link>
                )}
                {!backLink && onBackClick && (
                    <button onClick={onBackClick} className='flex flex-row items-center p-2 bg-transparent hover:bg-white rounded-full transition-colors duration-200'>
                        <ArrowLeft size={20} className='' />
                    </button>
                )}
                <h1 className='text-lg font-medium text-[#0b111e] overflow-hidden overflow-ellipsis max-w-[40vw] whitespace-nowrap'>{pageTitle}</h1>
            </div>

            <div className='w-1/3 flex flex-row gap-2 items-center justify-end'>
                {children}
                <Dropdown
                    label={(
                        <Avatar
                            size="sm"
                            rounded
                            theme={{
                                root: {
                                    base: 'flex flex-row items-center hover:bg-white border border-gray-300 rounded-full hover:border-white transition-colors duration-200 p-1',
                                    initials: {
                                        base: 'flex flex-row items-center justify-center bg-primary-500',
                                        text: 'text-white'
                                    }
                                },
                            }}
                            placeholderInitials={getNameInitials(identity.name)}
                        >
                            <span className='hidden lg:block text-sm font-medium leading-none px-2'>{identity.name}</span>
                        </Avatar>
                    )}
                    inline
                    arrowIcon={false}
                    className='z-50'
                >
                    <Dropdown.Item onClick={() => navigate(pathWithParams(RouteIndex.wallet, {}), { state: { from: window.location.pathname } })}>
                        Moje Peněženka
                    </Dropdown.Item>
                    <Dropdown.Item onClick={() => navigate(pathWithParams(RouteIndex.accountSettings, {}), { state: { from: window.location.pathname + window.location.search + window.location.hash } })}>
                        Nastavení účtu
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleFeedbackClick}>Odeslat zpětnou vazbu</Dropdown.Item>
                    <Dropdown.Item onClick={handleAboutClick}>O aplikaci</Dropdown.Item>
                    <Dropdown.Item onClick={logout}>Odhlásit se</Dropdown.Item>
                </Dropdown>
            </div>
        </div>
    );
};
